import { Fragment, useContext, useCallback, useMemo, useEffect } from 'react';
import { LinePath } from '@visx/shape';
import DataContext from "../../../context/DataContext.js";
import getScaledValueFactory from "../../../utils/getScaledValueFactory.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { LINESERIES_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import { BaseGlyphSeries } from "./BaseGlyphSeries.js";
import defaultRenderGlyph from "./defaultRenderGlyph.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function BaseLineSeries(_ref) {
  let {
    colorAccessor,
    curve,
    data,
    dataKey,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true,
    xAccessor,
    xScale,
    yAccessor,
    yScale,
    PathComponent = 'path',
    ...lineProps
  } = _ref;
  const {
    colorScale,
    theme
  } = useContext(DataContext);
  const getScaledX = useMemo(() => getScaledValueFactory(xScale, xAccessor), [xScale, xAccessor]);
  const getScaledY = useMemo(() => getScaledValueFactory(yScale, yAccessor), [yScale, yAccessor]);
  const isDefined = useCallback(d => isValidNumber(xScale(xAccessor(d))) && isValidNumber(yScale(yAccessor(d))), [xScale, xAccessor, yScale, yAccessor]);
  const color = colorScale?.(dataKey) ?? theme?.colors?.[0] ?? '#222';
  const ownEventSourceKey = `${LINESERIES_EVENT_SOURCE}-${dataKey}`;
  const eventEmitters = useSeriesEvents({
    dataKey,
    enableEvents,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });

  // render invisible glyphs for focusing if onFocus/onBlur are defined
  const captureFocusEvents = Boolean(onFocus || onBlur);
  const renderGlyphs = useCallback(_ref2 => {
    let {
      glyphs
    } = _ref2;
    return captureFocusEvents ? glyphs.map(glyph => /*#__PURE__*/_jsx(Fragment, {
      children: defaultRenderGlyph({
        ...glyph,
        color: 'transparent',
        onFocus: eventEmitters.onFocus,
        onBlur: eventEmitters.onBlur
      })
    }, glyph.key)) : null;
  }, [captureFocusEvents, eventEmitters.onFocus, eventEmitters.onBlur]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LinePath, {
      x: getScaledX,
      y: getScaledY,
      defined: isDefined,
      curve: curve,
      ...lineProps,
      children: _ref3 => {
        let {
          path
        } = _ref3;
        return /*#__PURE__*/_jsx(PathComponent, {
          stroke: colorAccessor?.(dataKey) ?? color,
          strokeWidth: 2,
          fill: "transparent",
          strokeLinecap: "round" // without this a datum surrounded by nulls will not be visible
          ,
          ...lineProps,
          d: path(data) || '',
          ...eventEmitters
        });
      }
    }), captureFocusEvents && /*#__PURE__*/_jsx(BaseGlyphSeries, {
      dataKey: dataKey,
      data: data,
      xAccessor: xAccessor,
      yAccessor: yAccessor,
      xScale: xScale,
      yScale: yScale,
      renderGlyphs: renderGlyphs
    })]
  });
}
export default function BaseBaseLineSeriesWithRegisteredData(props) {
  const {
    dataKey,
    data,
    xAccessor,
    yAccessor
  } = props;
  const {
    xScale,
    yScale,
    dataRegistry
  } = useContext(DataContext);
  useEffect(() => {
    if (dataRegistry) dataRegistry.registerData({
      key: dataKey,
      data,
      xAccessor,
      yAccessor
    });
    return () => dataRegistry?.unregisterData(dataKey);
  }, [dataRegistry, dataKey, data, xAccessor, yAccessor]);
  const registryEntry = dataRegistry?.get(dataKey);

  // if scales or data are not available in context, render nothing
  if (!xScale || !yScale || !registryEntry) return null;

  // otherwise pass props + over-write data/accessors
  return /*#__PURE__*/_jsx(BaseLineSeries, {
    ...props,
    xScale: xScale,
    yScale: yScale,
    data: registryEntry.data,
    xAccessor: registryEntry.xAccessor,
    yAccessor: registryEntry.yAccessor
  });
}